import React, { useCallback, useEffect, useState } from 'react';

import { Badge, List, Popover, Tooltip } from 'antd';
import { Spinner } from 'components';
import { type User } from 'models/user';
import { AiOutlineBell } from 'react-icons/ai';

import { LoadMoreTrigger } from './LoadMoreTrigger/LoadMoreTrigger';
import { NotificationItem } from './NotificationItem/NotificationItem';
import styles from './NotificationsWidget.module.css';
import { notificationsApi } from '../../api/notificationsApi';
import { type Notification } from '../../api/types/notification';

interface NotificationsWidgetProps {
  currentUser: User;
}

export const NotificationsWidget = ({ currentUser }: NotificationsWidgetProps) => {
  const [fetchNotifications, { data: fetchNotificationsResponse, isFetching: isNotificationsFetching }] =
    notificationsApi.useLazyFetchNotificationsQuery();

  const { data: hasUnreadNotifications, refetch: checkHasUnreadNotifications } =
    notificationsApi.useCheckUnreadNotificationsQuery();

  const [readNotifications] = notificationsApi.useReadNotificationsMutation();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [page, setPage] = useState<number>(1);
  const [notificationsToRead, setNotificationsToRead] = useState<Set<number>>(new Set());

  const hasMore =
    fetchNotificationsResponse?.pagination.page !== fetchNotificationsResponse?.pagination.pageCount;

  const markAsRead = useCallback((id: number) => {
    setNotificationsToRead((prev) => new Set(prev).add(id));
  }, []);

  useEffect(() => {
    if (notificationsToRead.size === 0) return;

    const timeout = setTimeout(() => {
      readNotifications({ ids: Array.from(notificationsToRead) })
        .unwrap()
        .then(() => setNotificationsToRead(new Set()))
        .catch((e) => {
          console.log('Ошибка прочтения уведомлений');
          console.error(e);
        });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [notificationsToRead]);

  useEffect(() => {
    if (page === 1) return;

    fetchNotifications({ page }, true)
      .unwrap()
      .then((res) => {
        setNotifications((prev) => [...prev, ...res.data]);
      })
      .catch((e) => {
        console.log('Ошибка получения уведомлений');
        console.error(e);
      });
  }, [page]);

  useEffect(() => {
    const ONE_HOUR = 3600000;
    const recheck = setInterval(checkHasUnreadNotifications, ONE_HOUR);

    return () => {
      clearInterval(recheck);
    };
  }, []);

  const openNotifications = () => {
    setPage(1);
    setNotifications([]);
    fetchNotifications({ page: 1 }, false)
      .unwrap()
      .then((res) => setNotifications(res.data))
      .catch((e) => {
        console.log('Ошибка получения уведомлений');
        console.error(e);
      });
  };

  const content = (
    <List
      className={styles.content}
      dataSource={notifications}
      renderItem={(item, index) => (
        <>
          <NotificationItem notification={item} onRead={markAsRead} currentUser={currentUser} />
          {notifications && index === notifications.length - 1 && hasMore && (
            <LoadMoreTrigger onInView={() => setPage((p) => p + 1)} />
          )}
        </>
      )}
    />
  );

  const contentFallback = (
    <div className={styles.contentFallback}>
      <Spinner />
    </div>
  );

  return (
    <Tooltip title={window.innerWidth < 820 ? undefined : 'Уведомления'} mouseEnterDelay={0.4}>
      <Popover
        overlayClassName={styles.popover}
        content={isNotificationsFetching && page === 1 ? contentFallback : content}
        title={'Центр уведомлений 📬'}
        trigger={'click'}
        destroyTooltipOnHide={true}
      >
        <Badge dot={hasUnreadNotifications} size={'small'}>
          <AiOutlineBell onClick={openNotifications} width={20} height={20} className={styles.icon} />
        </Badge>
      </Popover>
    </Tooltip>
  );
};
